/**
 * Taskpane entry point (the "taskpane" webpack entry, loaded by
 * taskpane.html in both Word and Excel). Responsibilities:
 *  - wait for Office.onReady, then mount the host's app
 *  - pick + live-update the Fluent theme from the Office host theme
 *  - lazy-load WordApp/ExcelApp so each host downloads only its own chunk
 *  - mount the cross-tab state providers (they sit OUTSIDE the tab switcher
 *    so screen state survives tab changes)
 */
import * as React from "react";
import { createRoot } from "react-dom/client";
import { FluentProvider, Spinner, Theme, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { FileViewProvider } from "./word/hooks/fileViewHook";
import { FillPlaceholdersProvider } from "./word/hooks/fillPlaceholdersHook";

// Per-host code splitting: Word users never download the Excel screens and
// vice versa. Each import lands in its own webpack chunk, fetched on demand.
const WordApp = React.lazy(() => import("./word/WordApp"));
const ExcelApp = React.lazy(() => import("./excel/ExcelApp"));


/* global document, Office, module, require, HTMLElement */

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const isDarkColor = (hex: string | undefined): boolean => {
  if (!hex) return false;
  const value = hex.replace("#", "");
  if (value.length < 6) return false;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b < 128;
};

// Match the Office host's light/dark mode. Newer hosts expose isDarkTheme
// directly; older ones only give theme colors, so fall back to the luminance
// of the body background, then to the OS preference outside Office.
const detectHostTheme = (): Theme => {
  const officeTheme = Office.context?.officeTheme as (Office.OfficeTheme & { isDarkTheme?: boolean }) | undefined;
  let dark: boolean;
  if (officeTheme && typeof officeTheme.isDarkTheme === "boolean") {
    dark = officeTheme.isDarkTheme;
  } else if (officeTheme?.bodyBackgroundColor) {
    dark = isDarkColor(officeTheme.bodyBackgroundColor);
  } else {
    dark = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }
  return dark ? webDarkTheme : webLightTheme;
};

const ThemedApp: React.FC = () => {
  const [theme, setTheme] = React.useState<Theme>(detectHostTheme);

  React.useEffect(() => {
    const handler = () => setTheme(detectHostTheme());
    try {
      Office.context?.document?.addHandlerAsync?.(Office.EventType.OfficeThemeChanged, handler);
    } catch {
      // Host doesn't support theme-change events; the theme picked at launch stands.
    }
    return () => {
      try {
        Office.context?.document?.removeHandlerAsync?.(Office.EventType.OfficeThemeChanged, { handler });
      } catch {
        // ignore
      }
    };
  }, []);

  return (
    <FluentProvider theme={theme} style={{ height: "100%" }}>
      <FileViewProvider>
        <FillPlaceholdersProvider>
          <React.Suspense
            fallback={
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Spinner labelPosition="below" label="Loading… Please wait" />
              </div>
            }
          >
            {Office.context.host === Office.HostType.Word && <WordApp />}
            {Office.context.host === Office.HostType.Excel && <ExcelApp />}
          </React.Suspense>
        </FillPlaceholdersProvider>
      </FileViewProvider>
    </FluentProvider>
  );
};

/* Render application after Office initializes */
Office.onReady(() => {
  root?.render(<ThemedApp />);
});

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(NextApp);
  });
}
